/* ==========================================================================
   as-collection-tile-video.js
   Collection grid — play the rollover clip on hover.

   The tile renders the video with preload="none", so without this it sits on
   its poster forever: CSS can fade a <video> in, but it cannot press play.

   HOVER ONLY. Bound behind (hover: hover) and (pointer: fine), so touch never
   reaches it — the notes are explicit that mobile is a still with tap-to-view
   and no autoplay. A phone that reports hover (some hybrids) still has to move
   a real pointer over the tile, which is the behaviour we want anyway.

   ONE AT A TIME, AND RELEASED ON LEAVE. Leaving the tile pauses, rewinds and
   calls load(), which hands the decoder back. A four-across grid scrolled for a
   while would otherwise leave a trail of paused-but-decoded videos behind it,
   and mobile Safari gives you about six before it starts refusing to play new
   ones.

   THE PLAY PROMISE. play() is async and rejects if the element is paused
   before it resolves — which is exactly what a quick mouse-over-and-away does.
   Unhandled, that is a console error on every flick of the cursor, so the
   promise is caught and a pending play is tracked so leave can wait for it.
   ========================================================================== */

(function () {
  'use strict';

  var CAN_HOVER = window.matchMedia('(hover: hover) and (pointer: fine)');
  var REDUCED = window.matchMedia('(prefers-reduced-motion: reduce)');

  function play(tile) {
    var video = tile.querySelector('[data-as-tile-video]');
    if (!video || REDUCED.matches) return;

    // preload="none" means there is nothing buffered until we ask.
    if (video.preload === 'none') {
      video.preload = 'auto';
      video.load();
    }

    video.muted = true; // belt and braces: an unmuted play() is refused outright
    var attempt = video.play();

    if (attempt && typeof attempt.then === 'function') {
      tile._asPending = attempt.then(
        function () {
          tile._asPending = null;
        },
        function () {
          // Autoplay refused, or we were paused mid-start. Neither is an error
          // worth surfacing — the poster is still showing and nothing is broken.
          tile._asPending = null;
        }
      );
    }
  }

  function stop(tile) {
    var video = tile.querySelector('[data-as-tile-video]');
    if (!video) return;

    var reset = function () {
      video.pause();
      video.currentTime = 0;
      // Releases the decoder; the poster takes over again.
      video.preload = 'none';
      video.load();
    };

    if (tile._asPending) {
      tile._asPending.then(reset, reset);
    } else {
      reset();
    }
  }

  function bind(root) {
    (root || document).querySelectorAll('[data-as-tile]').forEach(function (tile) {
      if (tile.dataset.asTileVideoBound === '1') return;
      if (!tile.querySelector('[data-as-tile-video]')) return;
      tile.dataset.asTileVideoBound = '1';

      tile.addEventListener('mouseenter', function () {
        if (CAN_HOVER.matches) play(tile);
      });
      tile.addEventListener('mouseleave', function () {
        stop(tile);
      });

      // Keyboard parity: tabbing to the product link is the same intent as
      // hovering the tile, and without this the clip is mouse-only.
      tile.addEventListener('focusin', function () {
        if (CAN_HOVER.matches) play(tile);
      });
      tile.addEventListener('focusout', function () {
        if (!tile.contains(document.activeElement)) stop(tile);
      });
    });
  }

  function start() {
    bind(document);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', start);
  } else {
    start();
  }

  // The grid is re-rendered by the Section Rendering API whenever a filter or
  // sort changes, which replaces every tile — so binding once is not enough.
  document.addEventListener('shopify:section:load', start);

  /*
    THE EVENT NAME HAS TO MATCH THE ONE ACTUALLY FIRED.

    This listened for 'as:collection:updated', which nothing in the theme
    dispatches — so it never ran once. The AJAX pagination in
    main-collection-product-grid.liquid fires 'collection:ajax-paginated'.
    Both are kept: the first costs nothing and leaves a hook if a future
    caller wants it, the second is the one that does the work today.
  */
  document.addEventListener('as:collection:updated', start);
  document.addEventListener('collection:ajax-paginated', start);

  /*
    OBSERVE THE CONTAINER, NOT THE GRID.

    Both the facet render and the AJAX pagination do

        ProductGridContainer.innerHTML = …

    which destroys #product-grid and builds a new one. An observer attached
    to #product-grid was therefore watching a node that had been thrown
    away — it fired for filters on the first render and never again, which
    is why page 2 had no hover video until a hard refresh put a fresh
    observer on the fresh node.

    #ProductGridContainer is the element being written INTO, so it survives
    every swap and one observer covers pagination and filtering alike.
  */
  if (window.MutationObserver) {
    var watch = function () {
      var el =
        document.getElementById('ProductGridContainer') ||
        document.getElementById('product-grid');
      if (!el) return;

      /* One innerHTML assignment emits a burst of mutation records. bind()
         is idempotent, but running it per record on a 24-tile grid is
         needless work — collapse the burst into a single pass. */
      var queued = false;
      new MutationObserver(function () {
        if (queued) return;
        queued = true;
        requestAnimationFrame(function () {
          queued = false;
          bind(el);
        });
      }).observe(el, { childList: true, subtree: true });
    };
    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', watch);
    } else {
      watch();
    }
  }
})();
